import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Home, Wallet, BookOpen, Menu, ListChecks, KeyRound,
  Share2, User as UserIcon, Trophy, Dices,
  Receipt, HelpCircle, Star, Sparkles,
  ScrollText, BookMarked, Gift
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { useAuth } from "../../lib/auth";
import PullToRefresh from "../PullToRefresh";
import ReddyBrand from "../ReddyBrand";

// Bottom nav — same items as before, premium fresher icon designs
const NAV_ITEMS = [
  { to: "/my-bids",  icon: ScrollText,  label: "My Bids" },
  { to: "/passbook", icon: BookMarked,  label: "Passbook" },
  { to: "/",         icon: Home,        label: "Home" },
  { to: "/refer",    icon: Gift,        label: "Refer & Earn" },
  { to: "/rates",    icon: Trophy,      label: "Game Rate" },
];

const DRAWER_ITEMS = [
  { to: "/",                    icon: Home,        label: "Home" },
  { to: "/profile",             icon: UserIcon,    label: "My Profile" },
  { to: "/withdraw",            icon: Wallet,      label: "Withdraw Funds" },
  { to: "/my-bids",             icon: ListChecks,  label: "Bid History" },
  { to: "/casino-history",      icon: Dices,       label: "Casino History" },
  { to: "/passbook",            icon: Receipt,     label: "Transaction History" },
  { to: "/my-bids?filter=won",  icon: Trophy,      label: "Win History" },
  { to: "/bonus-history",       icon: Sparkles,    label: "Bonus History" },
  { to: "/rates",               icon: Star,        label: "Market Rate" },
  { to: "/refer",               icon: Share2,      label: "Reddy Refer" },
  { to: "/support",             icon: HelpCircle,  label: "Help & Guide" },
  { to: "/mpin",                icon: KeyRound,    label: "Change Password" },
];

export default function MobileLayout({ children, hideBottomNav, hideHeader: hideHeaderProp = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Allow pages (e.g. MarketDetail) to render their own custom top bar.
  const showHeader = !hideHeaderProp;
  const showBottomNav = !hideBottomNav;

  return (
    <div className={`mobile-shell max-w-md mx-auto relative ${showBottomNav ? "pb-24" : "pb-4"} min-h-screen bg-brand-radial`} data-testid="user-shell">
      {/* Premium Royal Navy Header — rock-solid sticky */}
      {showHeader && (
      <header
        className="sticky top-0 z-40 text-white"
        style={{
          background: "linear-gradient(180deg, #0b1a3a 0%, #0a1430 70%, #080f26 100%)",
          borderBottom: "1px solid rgba(34,211,238,0.25)",
          boxShadow: "0 6px 20px rgba(0,0,0,0.45), 0 0 0 1px rgba(34,211,238,0.08) inset",
        }}
      >
        <div className="flex items-center justify-between px-2 sm:px-3 h-16 relative gap-1.5 sm:gap-2">
          <div className="flex items-center gap-1 sm:gap-1.5 min-w-0 flex-1 overflow-hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="open-drawer-btn" className="shrink-0 p-2 -ml-1 hover:bg-white/10 rounded-lg transition active:scale-90">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 p-0 border-r border-cyan-400/20"
                style={{ background: "linear-gradient(180deg, #0a1430 0%, #060a1b 100%)" }}>
                <div className="bg-royal-radial p-6 text-center text-white relative overflow-hidden">
                  <div className="absolute inset-0 pattern-grid opacity-30" />
                  <div className="relative">
                    <ReddyBrand variant="stacked" />
                    <div className="mt-3 text-xs text-cyan-100/90 font-semibold" data-testid="drawer-user-name">{user?.name || "Player"}</div>
                    {user?.mobile && <div className="text-xs text-cyan-200/80" data-testid="drawer-user-mobile">+91 {user.mobile}</div>}
                    <div className="mt-3 inline-flex items-center gap-2 bg-gold-gradient text-blue-900 px-4 py-1.5 rounded-full shadow-lg font-bold">
                      <Wallet className="w-4 h-4" />
                      <span className="text-sm tabular-nums" data-testid="drawer-balance">{user?.wallet_balance ?? 0} pts</span>
                    </div>
                  </div>
                </div>
                <nav className="py-2 max-h-[calc(100vh-220px)] overflow-y-auto">
                  {DRAWER_ITEMS.map(({ to, icon: Icon, label }) => (
                    <Link
                      key={to + label}
                      to={to}
                      onClick={() => setOpen(false)}
                      data-testid={`drawer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`flex items-center gap-3 px-5 py-3 text-sm transition border-b border-cyan-400/10 ${location.pathname === to ? "text-amber-300 font-bold bg-cyan-400/10" : "text-cyan-100/90 hover:bg-cyan-400/5"}`}
                    >
                      <Icon className={`w-5 h-5 ${location.pathname === to ? "text-amber-300" : "text-cyan-300"}`} />
                      <span className="text-base">{label}</span>
                    </Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
            <Link to="/" className="inline-flex items-center min-w-0 overflow-hidden" data-testid="header-brand-link">
              <ReddyBrand variant="compact" size={34} />
            </Link>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <div
              className="flex items-center gap-1.5 sm:gap-2 bg-gold-gradient text-blue-900 pl-2.5 sm:pl-3 pr-1.5 sm:pr-2 py-1.5 rounded-full shadow-lg btn-shine font-bold"
              data-testid="header-wallet"
            >
              <Wallet className="w-4 h-4 shrink-0" />
              <span className="text-xs sm:text-sm tabular-nums whitespace-nowrap">₹{user?.wallet_balance ?? 0}</span>
              <Link
                to="/deposit"
                data-testid="header-wallet-add"
                aria-label="Add funds"
                className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-900 text-yellow-300 flex items-center justify-center shadow-inner active:scale-90 transition shrink-0"
              >
                <span className="text-sm sm:text-base leading-none font-black">+</span>
              </Link>
            </div>
          </div>
        </div>
        {/* Gold accent line under header */}
        <div className="h-0.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
      </header>
      )}

      {/* Page content — wrapped in PullToRefresh so native pull-down refreshes the app */}
      <PullToRefresh>
        <main
          className={`${showHeader ? "px-3 py-3" : "px-0 pt-0"} relative`}
          style={{ minHeight: "calc(100vh - 160px)" }}
          data-testid="page-content"
        >{children}</main>
      </PullToRefresh>

      {/* Premium Bottom Nav — fixed, with floating gold Home circle (matches user screenshot) */}
      {showBottomNav && (
        <nav
          className="fixed bottom-0 left-0 right-0 z-[60] max-w-md mx-auto"
          data-testid="bottom-nav"
          style={{
            background: "linear-gradient(180deg, #0a1430 0%, #060a1b 100%)",
            borderTop: "1px solid rgba(34,211,238,0.3)",
            boxShadow: "0 -8px 24px rgba(0,0,0,0.5), 0 -1px 0 rgba(251,191,36,0.3) inset",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          {/* Gold accent hairline on top */}
          <div
            className="h-[2px] w-full"
            style={{
              background: "linear-gradient(90deg, transparent 0%, rgba(251,191,36,0.9) 25%, rgba(251,191,36,0.9) 75%, transparent 100%)",
            }}
          />
          <div className="flex items-stretch justify-between h-16 px-1">
            {NAV_ITEMS.map(({ to, icon: Icon, label }) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to + label}
                  to={to}
                  data-testid={`bottom-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="relative flex-1 flex flex-col items-center justify-center gap-1 active:scale-95 transition"
                  aria-label={label}
                >
                  {/* Active top indicator */}
                  {active && (
                    <span
                      className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-10 rounded-full"
                      style={{
                        background: "linear-gradient(90deg, #22d3ee, #fbbf24, #22d3ee)",
                        boxShadow: "0 0 10px rgba(251,191,36,0.8), 0 0 18px rgba(34,211,238,0.5)",
                      }}
                    />
                  )}
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center transition"
                    style={
                      active
                        ? {
                            background: "linear-gradient(135deg, rgba(34,211,238,0.18) 0%, rgba(251,191,36,0.12) 100%)",
                            border: "1px solid rgba(34,211,238,0.5)",
                            boxShadow: "0 0 14px rgba(34,211,238,0.35)",
                          }
                        : undefined
                    }
                  >
                    <Icon
                      className={`w-[22px] h-[22px] ${active ? "text-yellow-300" : "text-cyan-100/80"}`}
                      strokeWidth={active ? 2.6 : 2}
                      style={active ? { filter: "drop-shadow(0 0 4px rgba(251,191,36,0.6))" } : undefined}
                    />
                  </div>
                  <span
                    className={`text-[10px] font-black tracking-wide leading-none ${
                      active ? "text-yellow-300" : "text-cyan-100/80"
                    }`}
                  >
                    {label}
                  </span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
